/**
 * Allys Property Grid
 * 
 * Theme-specific implementation of the PropertyGrid with Airbnb-inspired design
 */

'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useState } from 'react';
import { PropertyGrid as CorePropertyGrid, type PropertyGridProps } from '../../../../src/components/properties';
import type { Property } from '../../../../src/lib/api/clients/properties';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { extractBestPriceSimple } from '@/lib/utils/price';

interface AllysPropertyGridProps extends Omit<PropertyGridProps, 'renderCard'> {
  /**
   * Optional section title
   */
  title?: string;
  
  /**
   * Optional section subtitle
   */
  subtitle?: string;
  
  /**
   * Show/hide view all button
   * @default false
   */
  showViewAll?: boolean;
  
  /**
   * Link for view all button
   */
  viewAllLink?: string;
  
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
  
  /**
   * Base path for property links (e.g., '/site/org-123' for tenant sites)
   * If not provided, defaults to '/{locale}'
   */
  basePath?: string;
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Property Grid Component
 * 
 * Modern, clean property grid with Airbnb-inspired cards
 */
export const AllysPropertyGrid = memo(function AllysPropertyGrid({
  properties, 
  columns = 3,
  maxRows = 3,
  title,
  subtitle,
  showViewAll = false,
  viewAllLink = '/properties',
  gap = 'lg',
  messages,
  locale: _locale,
  basePath,
  ...props
}: AllysPropertyGridProps) {
  // Default basePath to /{locale} if not provided
  const effectiveBasePath = basePath || `/${_locale || 'en'}`;
  // Get translations from messages
  const featuredProps = messages?.featuredProperties as Record<string, unknown> | undefined;
  const displayTitle = title !== undefined ? title : safeGetString(featuredProps?.title, 'Featured Properties');
  const displaySubtitle = subtitle !== undefined ? subtitle : safeGetString(featuredProps?.subtitle, 'Discover our handpicked selection of premium properties');
  const viewAllText = safeGetString(featuredProps?.viewAll, 'View All Properties');
  const viewText = safeGetString(featuredProps?.view, 'View');
  
  // Get property label translations
  const propertyLabelsObj = featuredProps?.labels as Record<string, unknown> | undefined;
  const propertyLabels = {
    newDevelopment: safeGetString(propertyLabelsObj?.newDevelopment, 'New Development'),
    limitedTime: safeGetString(propertyLabelsObj?.limitedTime, 'Limited Time'),
    hotDeal: safeGetString(propertyLabelsObj?.hotDeal, 'Hot Deal'),
    justListed: safeGetString(propertyLabelsObj?.justListed, 'Just Listed'),
  };
  
  return (
    <section id="content-after-search" className={`relative ${(displayTitle || displaySubtitle) ? 'py-16 lg:py-24' : 'py-0'} bg-transparent`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Section Header - Only show when title or subtitle is provided */}
        {(displayTitle || displaySubtitle) && (
          <div className="relative mb-12 lg:mb-16">
            {/* Decorative accent - Gold */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-24 h-1.5 bg-linear-to-r from-[#BBA339] to-[#E4CE4A] rounded-full shadow-lg shadow-[#BBA339]/20" />
            
            <div className="text-center">
              {displayTitle && (
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-(--color-text) mb-4 tracking-tight leading-[1.1] font-inter">
                  {displayTitle}
                </h2>
              )}
              {displaySubtitle && (
                <p className="text-lg lg:text-xl text-(--color-text-muted) max-w-3xl mx-auto font-light leading-relaxed font-inter">
                  {displaySubtitle}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Property Grid */}
        <CorePropertyGrid
          properties={properties}
          columns={columns}
          maxRows={maxRows}
          gap={gap}
          renderCard={(property: Property, index: number) => (
            <AllysPropertyCard property={property} index={index} viewText={viewText} propertyLabels={propertyLabels} basePath={effectiveBasePath} />
          )}
          {...props}
        />

        {/* View All Button */}
        {showViewAll && (
          <div className="mt-12 lg:mt-16 text-center">
            <Link
              href={viewAllLink}
              className="group relative inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-(--color-primary) hover:bg-(--color-primary)/90 text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl overflow-hidden font-inter"
            >
              <span className="relative z-10">{viewAllText}</span>
              <svg className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
              {/* Shine effect on hover */}
              <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
});

/**
 * Allys Property Card - Modern, professional design
 */
function AllysPropertyCard({ property, index, viewText, propertyLabels, basePath }: { 
  property: Property; 
  index: number; 
  viewText: string;
  propertyLabels: Record<string, string>;
  basePath: string;
}) {
  const [isFavorited, setIsFavorited] = useState(false);
  const [showHeartPulse, setShowHeartPulse] = useState(false);
  
  // Debug: Log property data received by the card
  if (process.env.NODE_ENV === 'development' && index === 0) {
    // eslint-disable-next-line no-console -- Development debugging
    console.log('[AllysPropertyCard] 🎴 Card received property data:', {
      id: property.id,
      name: property.name,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      totalFloorArea: property.totalFloorArea,
      totalFloorAreaUnit: property.totalFloorAreaUnit,
      landArea: property.landArea,
      landAreaUnit: property.landAreaUnit,
      garageCount: property.garageCount,
      carportCount: property.carportCount,
      openParkingCount: property.openParkingCount,
      parkingSpaces: property.parkingSpaces,
      currency: property.currency,
      attributeValues: property.attributeValues,
      askingPriceValue: property.attributeValues?.askingPrice?.valueNumber,
      // Show what fields are truthy
      hasData: {
        bedrooms: !!property.bedrooms,
        bathrooms: !!property.bathrooms,
        totalFloorArea: !!property.totalFloorArea,
        landArea: !!property.landArea,
        hasAnyParking: !!(property.garageCount || property.carportCount || property.openParkingCount || property.parkingSpaces),
        askingPrice: !!property.attributeValues?.askingPrice?.valueNumber,
      }
    });
  }
  
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsFavorited(!isFavorited);
    
    // Trigger pulse effect
    setShowHeartPulse(true);
    setTimeout(() => setShowHeartPulse(false), 600);
    
    // Here you would add logic to save to favorites
    // e.g., saveFavorite(property.id)
  };
  
  const firstImage = property.primaryImageUrl || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
  
  const { amount: askingPrice, currency } = extractBestPriceSimple(property);
  const hasPrice = askingPrice != null && askingPrice > 0;
    
  // Format floor area
  const floorArea = property.totalFloorArea 
    ? `${property.totalFloorArea.toLocaleString()} ${(property.totalFloorAreaUnit || 'sqft').toLowerCase()}`
    : null;
    
  // Format land area
  const landArea = property.landArea
    ? `${property.landArea.toLocaleString()} ${(property.landAreaUnit || 'sqft').toLowerCase()}`
    : null;
    
  // Calculate total parking
  const totalParking = (property.garageCount || 0) + (property.carportCount || 0) + (property.openParkingCount || 0) + (property.parkingSpaces || 0);
  
  // Check if we have any features to show
  const hasFeatures = property.bedrooms || property.bathrooms || floorArea || landArea || totalParking > 0;
  
  // Determine property label (for demonstration - would come from property data)
  const getPropertyLabel = () => {
    if (index === 0) return { text: propertyLabels.newDevelopment, color: 'from-[#BBA339] to-[#E4CE4A]' };
    if (index === 2) return { text: propertyLabels.limitedTime, color: 'from-[#243462] to-[#121C3B]' };
    if (index === 4) return { text: propertyLabels.hotDeal, color: 'from-[#BBA339] to-[#E4CE4A]' };
    if (index === 7) return { text: propertyLabels.justListed, color: 'from-[#243462] to-[#121C3B]' };
    return null;
  };
  
  const propertyLabel = getPropertyLabel();

  return (
    <Link 
      href={`${basePath}/properties/${property.id}`}
      className="group block"
    >
      <article className="h-full flex flex-col bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 overflow-hidden">
        {/* Image Container - Square with rounded corners */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          <Image
            src={firstImage}
            alt={property.name}
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-110"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            priority={index < 3}
          />
          
          {/* Gradient overlay for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          {/* Property Label - Modern Gradient Badge */}
          {propertyLabel && (
            <div className="absolute top-4 left-4 z-10">
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-xs font-bold bg-gradient-to-r ${propertyLabel.color} text-white shadow-lg backdrop-blur-sm font-inter`}>
                {propertyLabel.text}
              </span>
            </div>
          )}
          
          {/* Status Badge - Top Right (Airbnb style) */}
          {property.status && property.status !== 'ACTIVE' && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-900/90 backdrop-blur-md text-white shadow-md font-inter">
                {(property.status === 'ARCHIVED' || property.status === 'INACTIVE' || (property as unknown as Record<string, unknown>).listingStatus === 'SOLD') ? 'Sold' : property.status}
              </span>
            </div>
          )}
          
          {/* Favorite Button - Top Right (when no status) - Modern Micro-interaction */}
          {(!property.status || property.status === 'ACTIVE') && (
            <motion.button
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/95 backdrop-blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg hover:bg-white"
              onClick={handleFavoriteClick}
              aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              animate={isFavorited ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.3 }}
            >
              {/* Heart Icon with fill animation */}
              <motion.svg 
                className={`w-5 h-5 ${isFavorited ? 'text-red-500' : 'text-gray-700'} transition-colors duration-300`}
                fill={isFavorited ? "currentColor" : "none"}
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                strokeWidth={2}
                animate={isFavorited ? { 
                  scale: [1, 1.3, 1],
                } : {}}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </motion.svg>
              
              {/* Pulse rings on click */}
              <AnimatePresence>
                {showHeartPulse && (
                  <>
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-red-500"
                      initial={{ scale: 1, opacity: 0.8 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-red-400"
                      initial={{ scale: 1, opacity: 0.6 }}
                      animate={{ scale: 2.2, opacity: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                    />
                  </>
                )}
              </AnimatePresence>
              
              {/* Particle effect */}
              {showHeartPulse && (
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1 h-1 bg-red-500 rounded-full"
                      initial={{ 
                        x: 0, 
                        y: 0,
                        scale: 1,
                        opacity: 1
                      }}
                      animate={{ 
                        x: Math.cos((i * Math.PI * 2) / 8) * 20,
                        y: Math.sin((i * Math.PI * 2) / 8) * 20,
                        scale: 0,
                        opacity: 0
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  ))}
                </motion.div>
              )}
            </motion.button>
          )}
        </div>

        {/* Content - Modern, professional typography */}
        <div className="flex-1 flex flex-col p-5">
          {/* Property Name/Reference */}
          <h2 className="text-lg font-bold text-(--color-text) leading-tight line-clamp-1 group-hover:text-(--color-primary) transition-colors font-inter">
            {property.reference || property.name}
          </h2>
          
          {/* Description */}
          <p className="mt-2 text-sm text-(--color-text-muted) leading-relaxed line-clamp-2 font-light font-inter">
            {property.description || property.name}
          </p>
          
          {/* Key Features Bar - Always visible when features exist */}
          {hasFeatures && (
            <div className="mt-4 py-3 px-4 bg-gray-50 rounded-xl border border-gray-100 font-inter">
              <div className="flex items-center justify-start gap-4 flex-wrap">
                {/* Bedrooms */}
                {property.bedrooms && (
                  <div className="flex items-center gap-1.5 min-w-fit">
                    <svg className="w-4 h-4 text-[#243462]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                    </svg>
                    <span className="text-sm font-semibold text-(--color-text)">{property.bedrooms}</span>
                    <span className="text-xs text-gray-500">bed{property.bedrooms !== 1 ? 's' : ''}</span>
                  </div>
                )}
                
                {/* Bathrooms */}
                {property.bathrooms && (
                  <div className="flex items-center gap-1.5 min-w-fit">
                    <svg className="w-4 h-4 text-[#243462]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-semibold text-(--color-text)">{property.bathrooms}</span>
                    <span className="text-xs text-gray-500">bath{property.bathrooms !== 1 ? 's' : ''}</span>
                  </div>
                )}
                
                {/* Floor Area */}
                {floorArea && (
                  <div className="flex items-center gap-1.5 min-w-fit">
                    <svg className="w-4 h-4 text-[#243462]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                    </svg>
                    <span className="text-sm font-semibold text-(--color-text)">{floorArea}</span>
                  </div>
                )}
                
                {/* Land Area (if different from floor area) */}
                {landArea && !floorArea && (
                  <div className="flex items-center gap-1.5 min-w-fit">
                    <svg className="w-4 h-4 text-[#243462]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
                    </svg>
                    <span className="text-sm font-semibold text-(--color-text)">{landArea}</span>
                  </div>
                )}
                
                {/* Parking */}
                {totalParking > 0 && (
                  <div className="flex items-center gap-1.5 min-w-fit">
                    <svg className="w-4 h-4 text-[#243462]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                    </svg>
                    <span className="text-sm font-semibold text-(--color-text)">{totalParking}</span>
                    <span className="text-xs text-gray-500">parking</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Price & Action Section */}
          <div className="mt-auto pt-4 flex items-center justify-between border-t border-gray-100">
            {/* Price Display - Now uses FormatPrice for currency conversion */}
            <div className="flex flex-col">
              {hasPrice ? (
                <span className="text-2xl font-extrabold text-[#243462] font-inter">
                  <FormatPrice
                    amount={askingPrice}
                    currency={currency}
                    size="lg"
                    className="!text-[#243462]"
                  />
                </span>
              ) : (
                <span className="text-sm font-medium text-gray-500 italic font-inter">
                  Contact for price
                </span>
              )}
            </div>
            
            {/* View Button */}
            <span 
              className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-[#243462] text-white text-sm font-semibold hover:bg-[#1b294c] transition-all duration-300 cursor-pointer shadow-md hover:shadow-lg font-inter"
            >
              {viewText}
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}
